// Shared floor-side / call-direction rules for Control commands.
//
// Originally defined inline in setupWizard/steps/StepActions.tsx as
// CONTROL_DOOR_SIDE and CONTROL_CALL_DIRECTION. The monitoring control
// panel needs the same maps to decide which actions to render as a
// floor-button strip (per-floor commands like EnergizeCarCall) versus a
// single button (door-agnostic / bank-wide commands like VIP). Pulling
// the maps into a shared util keeps both consumers in sync — a schema
// rename or a new Control variant only needs to be classified here once.
//
// Sourced from `flatbuffer_schemas/schemas/ln2/control.fbs` via the
// generated Control enum. Front-door entries cover car-call
// energize/secure/lock/unlock + front hall-call up/down energize and
// lock/unlock; rear-door entries are the symmetric rear siblings. Any
// Control value not present here is treated as door-agnostic and
// therefore not floor-based at this layer.

import { Control } from "../flatbuffers/generated/control/control";
import { BankFloorOpening } from "./bankFloorOpenings";

/** Which side of the doorway a Control enum value applies to. */
export type DoorSide = "front" | "rear" | "any";

/** Hall-call direction; "none" disables top/bottom floor exclusion. */
export type CallDirection = "up" | "down" | "none";

/**
 * Door-side lookup for Control enum values. "front" / "rear" gate the
 * action on the matching door flag (front_door or rear_door). "any"
 * means the action is door-agnostic and lands on every floor.
 */
export const CONTROL_DOOR_SIDE = new Map<number, DoorSide>([
  [Control.EnergizeCarCall, "front"],
  [Control.EnergizeRearCarCall, "rear"],
  [Control.EnergizeUpHallCall, "front"],
  [Control.EnergizeUpRearHallCall, "rear"],
  [Control.EnergizeDownHallCall, "front"],
  [Control.EnergizeDownRearHallCall, "rear"],
  [Control.SecureCarCall, "front"],
  [Control.SecureRearCarCall, "rear"],
  [Control.CarCallLock, "front"],
  [Control.CarCallUnlock, "front"],
  [Control.RearCarCallLock, "rear"],
  [Control.RearCarCallUnlock, "rear"],
  [Control.UpHallCallLock, "front"],
  [Control.UpHallCallUnlock, "front"],
  [Control.UpRearHallCallLock, "rear"],
  [Control.UpRearHallCallUnlock, "rear"],
  [Control.DownHallCallLock, "front"],
  [Control.DownHallCallUnlock, "front"],
  [Control.DownRearHallCallLock, "rear"],
  [Control.DownRearHallCallUnlock, "rear"],
]);

/**
 * Hall-call direction for the floor-extremes rule: "up" calls cannot
 * originate from the top floor, "down" calls cannot originate from the
 * bottom floor. Car-call and door-control actions are "none" (allowed
 * on every floor with the matching door).
 */
export const CONTROL_CALL_DIRECTION = new Map<number, CallDirection>([
  [Control.EnergizeUpHallCall, "up"],
  [Control.EnergizeUpRearHallCall, "up"],
  [Control.UpHallCallLock, "up"],
  [Control.UpHallCallUnlock, "up"],
  [Control.UpRearHallCallLock, "up"],
  [Control.UpRearHallCallUnlock, "up"],
  [Control.EnergizeDownHallCall, "down"],
  [Control.EnergizeDownRearHallCall, "down"],
  [Control.DownHallCallLock, "down"],
  [Control.DownHallCallUnlock, "down"],
  [Control.DownRearHallCallLock, "down"],
  [Control.DownRearHallCallUnlock, "down"],
]);

/**
 * Returns the door side for a Control enum value, defaulting to "any"
 * for variants that aren't in CONTROL_DOOR_SIDE. Use this when deciding
 * which floor buttons to enable in a per-floor render.
 */
export function controlDoorSide(enumValue: number): DoorSide {
  return CONTROL_DOOR_SIDE.get(enumValue) ?? "any";
}

/**
 * Returns the call direction for a Control enum value, defaulting to
 * "none". Use this to skip the top/bottom floor button when the
 * direction would be physically meaningless (no up-call from the top,
 * no down-call from the bottom).
 */
export function controlCallDirection(enumValue: number): CallDirection {
  return CONTROL_CALL_DIRECTION.get(enumValue) ?? "none";
}

/**
 * Returns true when the given Control enum value should be rendered as
 * a per-floor button strip rather than a single button. A control is
 * "floor-based" when it has either a door side (front/rear) or a
 * direction (up/down) — both signals indicate the command takes a
 * floor position. Door-agnostic, position-less commands (VIP,
 * CloseDoor, etc.) return false and stay as single buttons.
 */
export function isFloorBasedControl(enumValue: number): boolean {
  return CONTROL_DOOR_SIDE.has(enumValue) || CONTROL_CALL_DIRECTION.has(enumValue);
}

/**
 * Whether one action should render a strip of floor buttons, preferring what
 * the SERVER says over what this file's table knows.
 *
 * `takesPosition` is `messages.position` projected onto the action row — the
 * column LiftCloud imports per message and preloads onto every action. It is
 * per-message, so unlike the tables above it covers adapter-only commands the
 * generated `Control` enum never declares, and it does not go stale when a new
 * positional command is added.
 *
 * The signal is deliberately ADDITIVE: `true` promotes a command the tables do
 * not classify, but `false` does NOT demote one they do. That asymmetry is the
 * whole safety argument. `messages.position` is imported data on a NOT NULL
 * column with no default, so a message that was simply never populated reads as
 * `false` — and treating that as authoritative would silently strip the floor
 * buttons off every hall call on a site whose import predates the column. A
 * missing floor button is not a cosmetic loss: floor buttons are how an operator
 * recalls a car with someone inside it.
 *
 * So this can only ever ADD a floor strip where there was none, never remove
 * one that works today — the same conservative shape `resolveCommandEnum` uses
 * for command resolution, and for the same reason.
 */
/**
 * The server's answer to "does this command take a floor?", mirroring
 * `Archive.ActionFloorScope`.
 *
 * UNKNOWN is a real answer meaning the server could not tell — no adapter key
 * matched the command, or the adapter's `floors_based` column is degenerate.
 * It must never be read as NO_FLOOR.
 */
export const ACTION_FLOOR_SCOPE_UNKNOWN = 0;
export const ACTION_FLOOR_SCOPE_FLOOR = 1;
export const ACTION_FLOOR_SCOPE_NO_FLOOR = 2;

/**
 * Whether an action renders the floor-button strip or a single plain button.
 *
 * Precedence, strongest first:
 *
 * 1. `floorScope` — the server's verdict, read off `keys.floors_based` for the
 *    adapter key that carries this command. This is the signal the operator
 *    actually configures: the Floor toggle in the wizard's LN1 Key Mappings.
 *    It is authoritative in BOTH directions, which is the point — Car To Lobby
 *    and Independent Service have it off and must render one button.
 * 2. `takesPosition` (`messages.position`) — promotion only, never demotion.
 *    Kept as a fallback for servers that predate `floorScope`, but it cannot be
 *    trusted to demote: on a real site it is true on 76 of 80 message rows,
 *    including both Car To Lobby and Independent Service, so reading it as
 *    authoritative is what put a floor strip on commands that take no floor.
 * 3. `isFloorBasedControl` — the hand-maintained enum table, last resort.
 *
 * UNKNOWN falls through rather than demoting. Showing a floor strip that is not
 * needed is untidy; hiding one that is needed stops an operator recalling a car
 * with someone inside.
 */
export function actionTakesFloor(
  takesPosition: boolean | null | undefined,
  enumValue: number | undefined,
  floorScope?: number | null,
): boolean {
  // No resolvable command means nothing to dispatch per floor, whatever the
  // server says about the message. The guard lives here rather than at the call
  // site so the card list and the floor list cannot drift on it.
  if (enumValue === undefined) return false;

  if (floorScope === ACTION_FLOOR_SCOPE_FLOOR) return true;
  if (floorScope === ACTION_FLOOR_SCOPE_NO_FLOOR) return false;

  if (takesPosition === true) return true;

  return isFloorBasedControl(enumValue);
}

/**
 * Resolve the set of (bank) floor positions an action should land on,
 * given the bank's per-floor opening map. Applies door-side filtering
 * (front vs rear) and the hall-call top/bottom exclusion. When the bank
 * has no preloaded floor data, returns every position so the seeder
 * degrades to its prior "all floors" behaviour rather than emitting zero
 * actions.
 *
 * For lift-scoped (units_based) entries the caller passes the per-lift
 * opening map via `liftDoors`; for bank-scoped (hall) entries `liftDoors`
 * is undefined and the bank-floor flags are used.
 *
 * Moved here from setupWizard/steps/StepActions.tsx, which re-exports it: the
 * scheduled-action floor picker renders exactly this eligibility as checkboxes,
 * and a picker that disagreed with the seeder about which floors a command can
 * reach would offer an operator a floor the command cannot act on.
 */
export function eligibleFloorPositions(
  enumValue: number,
  bankFloors: BankFloorOpening[],
  liftDoors?: Map<string, { front: boolean; rear: boolean }>,
): number[] {
  return eligibleFloorOpenings(enumValue, bankFloors, liftDoors).map((f) => f.position);
}

/**
 * The same eligibility, returning the FLOORS rather than their positions.
 *
 * Monitoring needs the records, not the numbers: it renders a button per floor
 * (labelled with the floor's name) and dispatches the live command by
 * `floor_index`, so a position list cannot be mapped back — on a bank whose
 * `position` column is degenerate every floor answers 0 and the mapping is
 * ambiguous. Identity travels on `id`, which is unique whatever the position
 * column contains.
 *
 * This is the one implementation of the door-side and extremes rules;
 * `eligibleFloorPositions` is a projection of it. The monitoring control panel
 * used to carry its own copy (`eligibleFloorsFor`), and that copy lacked the
 * degenerate-order guard below — so on a bank with an all-zero position column
 * every floor read as both the top and the bottom, every up/down hall-call floor
 * was excluded, and the card was dropped for having nothing to render. The
 * command was configured, visible in the wizard, and unreachable on the screen.
 */
export function eligibleFloorOpenings(
  enumValue: number,
  bankFloors: BankFloorOpening[],
  liftDoors?: Map<string, { front: boolean; rear: boolean }>,
): BankFloorOpening[] {
  if (bankFloors.length === 0) return [];
  const side = controlDoorSide(enumValue);
  const direction = controlCallDirection(enumValue);
  // Ranked by `order`, which is `position` on a healthy bank and the recovered
  // floor_no/floor_index on one whose position column is degenerate. The
  // EXTREMES are a question about height, so they read the rank; what the
  // function RETURNS is `position`, the only floor number the server matches.
  const ranks = bankFloors.map((f) => f.order);
  const topPos = Math.max(...ranks);
  const bottomPos = Math.min(...ranks);

  // Degenerate-order guard: >1 floor but every floor shares one position (the
  // order was never populated and could not be recovered upstream). We cannot
  // identify the physical top/bottom, so applying the extreme exclusion would
  // drop EVERY floor and silently omit the hall-call command. Skip the
  // exclusion and let door-side filtering decide — degrading to "all floors
  // eligible" rather than none. A genuine single-floor bank (length 1) still
  // applies the exclusion, correctly yielding zero eligible hall-call floors.
  const orderable = bankFloors.length < 2 || topPos !== bottomPos;

  return bankFloors.filter((floor) => {
    if (orderable && direction === "up" && floor.order === topPos) return false;
    if (orderable && direction === "down" && floor.order === bottomPos) return false;
    if (side === "any") return true;
    const doors = liftDoors?.get(floor.id) ?? { front: floor.front, rear: floor.rear };
    return side === "front" ? doors.front : doors.rear;
  });
}
